import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { decodeGeohash, formatCoordinates } from "../utils/geohash";
import { FaMapMarkerAlt, FaCode, FaCopy, FaCheck, FaTimes, FaWindowMinimize, FaCommentDots } from "react-icons/fa";
import { useAnalyticsBot } from "../context/AnalyticsBotContext";
import { ANALYTICS_EMPTY_STATE, ANALYTICS_EXAMPLE_QUERIES } from "../constants/analyticsCopy";

export default function AnalyticsQueryBot() {
  const { isOpen, isMinimized, chatHistory, setChatHistory, closeBot, minimizeBot, toggleBot } = useAnalyticsBot();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [displayedText, setDisplayedText] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [expandedResults, setExpandedResults] = useState({});
  const [loadedResults, setLoadedResults] = useState({});
  const [loadingMore, setLoadingMore] = useState({});
  const [showSql, setShowSql] = useState({}); // Track which messages show SQL
  const [copiedSql, setCopiedSql] = useState({}); // Track copied SQL
  const [uploadSpeedFilter, setUploadSpeedFilter] = useState({}); // Track upload speed filter per message
  const [sortByUpload, setSortByUpload] = useState({}); // Track sort order per message
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(null);
  const [exampleQueries, setExampleQueries] = useState(ANALYTICS_EXAMPLE_QUERIES);
  const messages = chatHistory;
  const setMessages = setChatHistory;

  const API_BASE = process.env.REACT_APP_API_SERVER_URL;

  useEffect(() => {
    if (!isOpen || !API_BASE) return;
    axios.get(`${API_BASE}/maiApi/analytics/suggestions`).then((res) => {
      if (res.data?.examples?.regular?.length) setExampleQueries(res.data.examples);
    }).catch(() => {});
  }, [isOpen, API_BASE]);

  const getSessionId = () => {
    if (!sessionIdRef.current) {
      sessionIdRef.current =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    }
    return sessionIdRef.current;
  };
  
  // Format summary text with bold/italic styling
  const formatSummaryText = (text) => {
    if (!text) return text;
    
    // Split by lines to process each line
    const lines = text.split('\n');
    
    return lines.map((line, lineIdx) => {
      if (!line.trim()) return <br key={lineIdx} />;
      
      // Check if line is a heading (starts with #, or is all caps, or ends with :)
      const isHeading = line.match(/^#+\s|^[A-Z][A-Z\s]+:$|^[A-Z][^:]*:$/) || 
                       (line.length < 50 && line.match(/^[A-Z]/) && !line.includes('•') && !line.includes('-'));
      
      // Format device IDs (patterns like TEST_DEVICE_001, 6597a8d8cf10319f, etc.)
      const deviceIdPattern = /\b([A-Z0-9_]{8,}|[a-f0-9]{12,})\b/g;
      
      // Format carrier names (Verizon, AT&T, T-Mobile, etc.)
      const carrierPattern = /\b(Verizon|AT&T|T-Mobile|Sprint|TMobile|ATT)\b/gi;
      
      // Format numbers with units (Mbps, %, etc.)
      const numberPattern = /\b(\d+\.?\d*)\s*(Mbps|%|ms|dBm|db)\b/gi;
      
      // Format bullet points
      if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
        const bullet = line.trim().startsWith('•') ? '•' : '-';
        const content = line.replace(/^[•\-]\s*/, '');
        
        // Process content for formatting
        const parts = [];
        let lastIndex = 0;
        let match;
        
        // Find all patterns and create formatted parts
        const allMatches = [];
        
        // Device IDs
        while ((match = deviceIdPattern.exec(content)) !== null) {
          allMatches.push({ type: 'device', start: match.index, end: match.index + match[0].length, text: match[0] });
        }
        deviceIdPattern.lastIndex = 0;
        
        // Carriers
        while ((match = carrierPattern.exec(content)) !== null) {
          allMatches.push({ type: 'carrier', start: match.index, end: match.index + match[0].length, text: match[0] });
        }
        carrierPattern.lastIndex = 0;
        
        // Numbers with units
        while ((match = numberPattern.exec(content)) !== null) {
          allMatches.push({ type: 'number', start: match.index, end: match.index + match[0].length, text: match[0] });
        }
        numberPattern.lastIndex = 0;
        
        // Sort matches by position
        allMatches.sort((a, b) => a.start - b.start);
        
        // Build formatted parts
        const formattedParts = [];
        let currentIndex = 0;
        
        allMatches.forEach((m) => {
          if (m.start > currentIndex) {
            formattedParts.push({ type: 'text', text: content.substring(currentIndex, m.start) });
          }
          formattedParts.push({ type: m.type, text: m.text });
          currentIndex = m.end;
        });
        
        if (currentIndex < content.length) {
          formattedParts.push({ type: 'text', text: content.substring(currentIndex) });
        }
        
        if (formattedParts.length === 0) {
          formattedParts.push({ type: 'text', text: content });
        }
        
        return (
          <div key={lineIdx} className="flex items-start gap-2 my-1">
            <span className="text-gray-600 font-semibold mt-0.5">{bullet}</span>
            <span className="flex-1">
              {formattedParts.map((part, partIdx) => {
                if (part.type === 'device') {
                  return <span key={partIdx} className="font-mono italic text-blue-700 font-medium">{part.text}</span>;
                } else if (part.type === 'carrier') {
                  return <span key={partIdx} className="italic text-purple-700 font-medium">{part.text}</span>;
                } else if (part.type === 'number') {
                  return <span key={partIdx} className="font-semibold text-green-700">{part.text}</span>;
                } else {
                  return <span key={partIdx}>{part.text}</span>;
                }
              })}
            </span>
          </div>
        );
      }
      
      // Format headings
      if (isHeading) {
        return (
          <div key={lineIdx} className="font-bold text-gray-900 text-base mt-3 mb-2">
            {line.replace(/^#+\s*/, '')}
          </div>
        );
      }
      
      // Regular line - still format devices, carriers, numbers
      const parts = [];
      let lastIndex = 0;
      const matches = [];
      let match;
      
      while ((match = deviceIdPattern.exec(line)) !== null) {
        matches.push({ type: 'device', ...match });
      }
      deviceIdPattern.lastIndex = 0;
      
      while ((match = carrierPattern.exec(line)) !== null) {
        matches.push({ type: 'carrier', ...match });
      }
      carrierPattern.lastIndex = 0;
      
      while ((match = numberPattern.exec(line)) !== null) {
        matches.push({ type: 'number', ...match });
      }
      numberPattern.lastIndex = 0;
      
      matches.sort((a, b) => a.index - b.index);
      
      matches.forEach((m) => {
        if (m.index > lastIndex) {
          parts.push({ type: 'text', text: line.substring(lastIndex, m.index) });
        }
        parts.push({ type: m.type, text: m[0] });
        lastIndex = m.index + m[0].length;
      });
      
      if (lastIndex < line.length) {
        parts.push({ type: 'text', text: line.substring(lastIndex) });
      }
      
      if (parts.length === 0) {
        return <div key={lineIdx}>{line}</div>;
      }
      
      return (
        <div key={lineIdx}>
          {parts.map((part, partIdx) => {
            if (part.type === 'device') {
              return <span key={partIdx} className="font-mono italic text-blue-700 font-medium">{part.text}</span>;
            } else if (part.type === 'carrier') {
              return <span key={partIdx} className="italic text-purple-700 font-medium">{part.text}</span>;
            } else if (part.type === 'number') {
              return <span key={partIdx} className="font-semibold text-green-700">{part.text}</span>;
            } else {
              return <span key={partIdx}>{part.text}</span>;
            }
          })}
        </div>
      );
    });
  };
  
  // Copy SQL to clipboard
  const copySqlToClipboard = (sql, messageId) => {
    navigator.clipboard.writeText(sql).then(() => {
      setCopiedSql(prev => ({ ...prev, [messageId]: true }));
      setTimeout(() => {
        setCopiedSql(prev => ({ ...prev, [messageId]: false }));
      }, 2000);
    });
  };
  
  // Handle "View More" button click
  const handleLoadMore = async (messageId, currentOffset) => {
    if (loadingMore[messageId]) return;
    
    setLoadingMore(prev => ({ ...prev, [messageId]: true }));
    
    try {
      const message = messages.find(m => (m.messageId || m.timestamp?.getTime()) === messageId);
      if (!message || !message.query) {
        console.error('Could not find original query for message');
        return;
      }
      
      const newLimit = 10;
      const newOffset = currentOffset;
      
      const response = await axios.post(`${API_BASE}/maiApi/analytics/query`, {
        query: message.query,
        limit: newLimit,
        offset: newOffset,
        sessionId: sessionIdRef.current || getSessionId(),
      });
      
      const newData = response.data;
      if (newData.success === false) {
        console.error('Load more failed:', newData.error);
        return;
      }
      
      setMessages(prev => prev.map(msg => {
        const msgId = msg.messageId || msg.timestamp?.getTime();
        if (msgId === messageId) {
          return {
            ...msg,
            results: [...(msg.results || []), ...(newData.results || [])],
            pagination: newData.pagination
          };
        }
        return msg;
      }));
      
      setLoadedResults(prev => ({
        ...prev,
        [messageId]: (prev[messageId] || 0) + (newData.results?.length || 0)
      }));
      
    } catch (err) {
      console.error('Error loading more results:', err);
      const errorMessage = {
        type: "error",
        content: `Failed to load more results: ${err.response?.data?.error || err.message}`,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoadingMore(prev => ({ ...prev, [messageId]: false }));
    }
  };

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, displayedText]);

  // Typing animation effect
  useEffect(() => {
    if (!isTyping) return;
    return () => {
      if (typingTimeoutRef.current) {
        clearInterval(typingTimeoutRef.current);
      }
    };
  }, [isTyping]);


  const startTypingAnimation = (fullText, messageIndex) => {
    setIsTyping(true);
    let currentIndex = 0;

    const typeInterval = setInterval(() => {
      if (currentIndex < fullText.length) {
        const partialText = fullText.substring(0, currentIndex + 1);
        setDisplayedText(partialText);
        
        setMessages((prev) => {
          const updated = [...prev];
          if (updated[messageIndex]) {
            updated[messageIndex] = {
              ...updated[messageIndex],
              content: partialText,
            };
          }
          return updated;
        });
        
        currentIndex++;
      } else {
        clearInterval(typeInterval);
        setIsTyping(false);
        
        setMessages((prev) => {
          const updated = [...prev];
          if (updated[messageIndex]) {
            updated[messageIndex] = {
              ...updated[messageIndex],
              content: fullText,
              isTyping: false,
            };
          }
          return updated;
        });
      }
    }, 5); // Fast typing speed (5ms per character for quick display)

    typingTimeoutRef.current = typeInterval;
  };

  const handleSubmit = async (e, queryOverride) => {
    if (e && e.preventDefault) e.preventDefault();
    const userQuery = (typeof queryOverride === 'string' ? queryOverride : query).trim();
    if (!userQuery || loading) return;
    setQuery("");
    setError(null);

    const userMessage = {
      type: "user",
      content: userQuery,
      timestamp: new Date(),
    };
    setMessages((prev) => [...prev, userMessage]);

    setLoading(true);

    try {
      const response = await axios.post(`${API_BASE}/maiApi/analytics/query`, {
        query: userQuery,
        limit: 10,
        offset: 0,
        sessionId: getSessionId(),
      });

      const data = response.data;
      setLoading(false);

      if (data.success === false) {
        let content = data.error;
        if (data.detail) content += `\n\nDetail: ${data.detail}`;
        if (data.suggestion) content += `\n\n${data.suggestion}`;
        setMessages((prev) => [
          ...prev,
          {
            type: "assistant",
            content,
            timestamp: new Date(),
          },
        ]);
        return;
      }

      const messageId = Date.now();
      const aiMessage = {
        type: "assistant",
        content: "",
        fullContent: data.answer_type === 'count' ? data.answer : (data.summary || "No summary available."),
        // C6 FIX: Backend doesn't echo the query back — use our local variable
        // instead of data.query (which is undefined), so Load More and
        // Comparison features can reference the original user input.
        query: userQuery,
        parsed: data.parsed,
        metric: data.metric,
        sql: data.sql,
        results: data.results,
        count: data.count,
        pagination: data.pagination,
        answer_type: data.answer_type,
        answer: data.answer,
        followUpQuestions: data.followUpQuestions,
        timestamp: new Date(),
        isTyping: true,
        messageId: messageId
      };

      setLoadedResults(prev => ({
        ...prev,
        [messageId]: data.results?.length || 0
      }));

      setMessages((prev) => {
        const newMessages = [...prev, aiMessage];
        if (data.summary) {
          setTimeout(() => {
            startTypingAnimation(data.summary, newMessages.length - 1);
          }, 100);
        } else {
          setTimeout(() => {
            setMessages((prevMsgs) => {
              const updated = [...prevMsgs];
              updated[updated.length - 1] = {
                ...updated[updated.length - 1],
                content: "No summary available.",
                isTyping: false,
              };
              return updated;
            });
          }, 100);
        }
        return newMessages;
      });
    } catch (err) {
      const error = err.response?.data?.error;
      const suggestion = err.response?.data?.suggestion;
      const detail = err.response?.data?.detail;
      let content = error || "Something went wrong. Please try again.";
      if (detail) content += `\n\nDetail: ${detail}`;
      if (suggestion) content += `\n\n${suggestion}`;
      setMessages((prev) => [
        ...prev,
        {
          type: "assistant",
          content,
          timestamp: new Date(),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleExampleClick = (example) => {
    setQuery(example);
    // Focus on the input box after setting the query
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  return (
    <>
      {/* Floating Button - Show when closed or minimized */}
      {(!isOpen || isMinimized) && (
        <button
          onClick={toggleBot}
          className="fixed bottom-6 right-6 w-14 h-14 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full shadow-2xl hover:from-blue-700 hover:to-blue-800 transition-all duration-300 flex items-center justify-center z-[1101] hover:scale-110 active:scale-95"
          title="Open Analytics Query Bot"
        >
          <FaCommentDots className="w-6 h-6" />
          {chatHistory.length > 0 && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-xs font-bold">
              {chatHistory.length}
            </span>
          )}
        </button>
      )}

      {/* Blurred Backdrop - Show when panel is open */}
      {isOpen && !isMinimized && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[1099] transition-opacity duration-300"
          onClick={minimizeBot}
        />
      )}
      
      {/* Side Panel */}
      <div 
        className={`fixed right-0 top-0 h-full w-full md:w-1/2 bg-white shadow-2xl z-[1100] flex flex-col transform transition-transform duration-300 ease-in-out ${
          isOpen && !isMinimized ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 flex items-center justify-between">
          <div className="flex-1">
            <h2 className="text-lg font-semibold">Analytics Query Assistant</h2>
            <p className="text-xs text-blue-100 mt-0.5">Ask questions about device performance</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={minimizeBot}
              className="p-1.5 hover:bg-blue-800 rounded transition-colors"
              title="Minimize"
            >
              <FaWindowMinimize className="w-4 h-4" />
            </button>
            <button
              onClick={closeBot}
              className="p-1.5 hover:bg-blue-800 rounded transition-colors"
              title="Close"
            >
              <FaTimes className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Chat Container - Full Height */}
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto px-3 py-4 space-y-4"
          style={{ scrollBehavior: "smooth" }}
        >
          {messages.length === 0 ? (
            <div className="mt-4">
              <div className="text-center mb-6">
                <div className="inline-block p-3 bg-blue-100 rounded-full mb-3">
                  <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-800 mb-1">{ANALYTICS_EMPTY_STATE.heading}</h2>
                <p className="text-sm text-gray-600">{ANALYTICS_EMPTY_STATE.description}</p>
              </div>
              <div className="space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-700 mb-2">Try these examples:</p>
                  <div className="space-y-2">
                    {exampleQueries.regular.map((example, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleExampleClick(example)}
                        className="w-full text-left p-2 text-sm bg-white rounded border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-all"
                      >
                        <span className="text-gray-700">{example}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="pt-3 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Compare:</p>
                  <div className="space-y-2">
                    {exampleQueries.compare.map((example, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleExampleClick(example)}
                        className="w-full text-left p-2 text-sm bg-purple-50 rounded border border-purple-200 hover:border-purple-500 hover:bg-purple-100 transition-all"
                      >
                        <span className="text-gray-700">{example}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {messages.map((message, idx) => {
                const messageId = message.messageId || message.timestamp?.getTime();
                return (
                <div
                  key={idx}
                  className={`flex gap-2 ${
                    message.type === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  {message.type === "assistant" && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                      CG
                    </div>
                  )}
                  <div
                      className={`max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                      message.type === "user"
                          ? "bg-gradient-to-br from-blue-600 to-blue-700 text-white shadow-md"
                        : message.type === "error"
                          ? "bg-red-50 text-red-800 border-2 border-red-200"
                          : "bg-white text-gray-800 shadow-md border border-gray-100"
                    }`}
                  >
                  {message.type === "user" ? (
                      <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                  ) : message.type === "error" ? (
                    <p className="whitespace-pre-wrap">
                      <strong>Error:</strong> {message.content}
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {/* Direct Answer for Count Queries - Prominent */}
                      {message.answer_type === 'count' && message.answer && (
                        <div className="rounded-xl bg-emerald-50/80 border border-emerald-200/80 p-3">
                          <p className="text-xs font-medium text-emerald-800/90 uppercase tracking-wide mb-1">Answer</p>
                          <p className="text-base text-gray-900 leading-snug font-medium">{message.answer}</p>
                          {message.results && message.results.length > 0 && message.results[0].yard_number != null && message.results[0].device_count != null && (
                            <p className="mt-2 text-xs text-gray-600">
                              Yard {message.results[0].yard_number} {message.results[0].yard_name ? `(${message.results[0].yard_name})` : ''} · {message.results[0].device_count} device{message.results[0].device_count !== 1 ? 's' : ''}
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Summary Card: headline + main answer text first, then compact stats */}
                      {message.answer_type !== 'count' && message.results && message.results.length > 0 && (() => {
                        const results = message.results;
                        const count = message.count || results.length;
                        
                        // Calculate statistics
                        const downloadValues = results.map(r => parseFloat(r.download_mbps || r.avg_download || 0)).filter(v => !isNaN(v) && v > 0);
                        const uploadValues = results.map(r => parseFloat(r.upload_mbps || r.avg_upload || 0)).filter(v => !isNaN(v) && v > 0);
                        const pingValues = results.map(r => parseFloat(r.ping_ms || r.avg_ping || 0)).filter(v => !isNaN(v) && v > 0);
                        const signalValues = results.map(r => parseFloat(r.lte_rsrp_dbm || r.avg_lte_rsrp || 0)).filter(v => !isNaN(v));
                        
                        // Group by yard and carrier
                        const yardSet = new Set();
                        const carrierSet = new Set();
                        results.forEach(r => {
                          if (r.yard_number) yardSet.add(r.yard_number);
                          if (r.carrier && r.carrier !== '-') carrierSet.add(r.carrier);
                        });
                        const isComparison = message.parsed?.intent === 'comparison';
                        
                        const downloadAvg = downloadValues.length > 0 ? downloadValues.reduce((a, b) => a + b, 0) / downloadValues.length : 0;
                        const downloadMin = downloadValues.length > 0 ? Math.min(...downloadValues) : 0;
                        const downloadMax = downloadValues.length > 0 ? Math.max(...downloadValues) : 0;
                        
                        const uploadAvg = uploadValues.length > 0 ? uploadValues.reduce((a, b) => a + b, 0) / uploadValues.length : 0;
                        const uploadMin = uploadValues.length > 0 ? Math.min(...uploadValues) : 0;
                        const uploadMax = uploadValues.length > 0 ? Math.max(...uploadValues) : 0;
                        
                        // Performance indicators
                        const getPerformanceColor = (value, type) => {
                          if (type === 'download') {
                            if (value >= 10) return 'text-green-600';
                            if (value >= 5) return 'text-yellow-600';
                            return 'text-red-600';
                          } else if (type === 'upload') {
                            if (value >= 5) return 'text-green-600';
                            if (value >= 2) return 'text-yellow-600';
                            return 'text-red-600';
                          } else if (type === 'signal') {
                            if (value >= -90) return 'text-green-600';
                            if (value >= -105) return 'text-yellow-600';
                            return 'text-red-600';
                          }
                          return 'text-gray-600';
                        };
                        
                        const contentValue = message.isTyping ? message.content : (message.content || message.fullContent);
                        const fullText = contentValue != null ? (typeof contentValue === 'string' ? contentValue : (typeof contentValue === 'object' ? JSON.stringify(contentValue) : String(contentValue))) : '';
                        const attributionMatch = typeof fullText === 'string' && fullText.match ? fullText.match(/\n\n\(Response by Copart Genie\)$/) : null;
                        const mainText = attributionMatch ? fullText.replace(/\n\n\(Response by Copart Genie\)$/, '') : fullText;
                        const hasAttribution = !!attributionMatch;

                        return (
                          <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
                            {isComparison && yardSet.size > 0 && (() => {
                              const carrierLabel = carrierSet.size > 0 ? Array.from(carrierSet).join(', ') : null;
                              const yardLabel = Array.from(yardSet).sort((a, b) => String(a).localeCompare(String(b))).map(y => `Yard ${y}`).join(' vs ');
                              return (
                                <div className="px-3 py-2 bg-slate-50 border-b border-slate-100">
                                  <h4 className="text-xs font-semibold text-slate-800">{carrierLabel ? `${carrierLabel} in ${yardLabel}` : `Comparing ${yardLabel}`}</h4>
                                </div>
                              );
                            })()}
                            {mainText && mainText.trim().length > 0 && (
                              <div className="px-3 py-3">
                                <p className="text-xs font-medium text-slate-600 uppercase tracking-wide mb-1">Summary</p>
                                <div className="text-sm text-gray-900 leading-relaxed">
                                  {message.isTyping ? (
                                    <><div className="whitespace-pre-line">{mainText}</div><span className="animate-pulse text-blue-600">▊</span></>
                                  ) : (
                                    <div>{formatSummaryText(mainText)}</div>
                                  )}
                                </div>
                                {hasAttribution && !message.isTyping && <p className="text-xs text-slate-500 mt-1">Response by Copart Genie</p>}
                              </div>
                            )}
                            <div className="px-3 pb-3 pt-0">
                              <div className="flex flex-wrap items-center gap-1.5 mb-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-medium">
                                  {isComparison && yardSet.size > 0 ? `${yardSet.size} yard${yardSet.size === 1 ? '' : 's'}` : isComparison && carrierSet.size > 0 ? `${carrierSet.size} carrier${carrierSet.size === 1 ? '' : 's'}` : `${count} device${count === 1 ? '' : 's'}`}
                                </span>
                                {yardSet.size > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 text-xs font-medium">{yardSet.size} yard{yardSet.size === 1 ? '' : 's'}</span>}
                                {carrierSet.size > 0 && <span className="inline-flex items-center px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 text-xs font-medium">{carrierSet.size} carrier{carrierSet.size === 1 ? '' : 's'}</span>}
                              </div>
                              <div className="flex flex-wrap gap-3 text-xs">
                                {downloadValues.length > 0 && (
                                  <span className="text-slate-600"><span className="text-slate-500">Download avg</span> <span className={`font-semibold ${getPerformanceColor(downloadAvg, 'download')}`}>{downloadAvg.toFixed(2)} Mbps</span></span>
                                )}
                                {uploadValues.length > 0 && (
                                  <span className="text-slate-600"><span className="text-slate-500">Upload avg</span> <span className={`font-semibold ${getPerformanceColor(uploadAvg, 'upload')}`}>{uploadAvg.toFixed(2)} Mbps</span></span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })()}
                      
                      {/* Fallback: Show summary text if no results yet */}
                      {(!message.results || message.results.length === 0) && (() => {
                        let contentValue = message.isTyping 
                          ? message.content 
                          : (message.content || message.fullContent);
                        
                        const fullText = contentValue != null 
                          ? (typeof contentValue === 'string' 
                              ? contentValue 
                              : (typeof contentValue === 'object' 
                                  ? JSON.stringify(contentValue) 
                                  : String(contentValue)))
                          : '';
                        
                        const attributionMatch = typeof fullText === 'string' && fullText.match 
                          ? fullText.match(/\n\n\(Response by Copart Genie\)$/) 
                          : null;
                        const mainText = attributionMatch 
                          ? fullText.replace(/\n\n\(Response by Copart Genie\)$/, '') 
                          : fullText;
                        const hasAttribution = !!attributionMatch;
                        
                        if (!mainText || mainText.trim().length === 0) return null;
                        
                        return (
                          <div className="leading-relaxed text-gray-800">
                            {message.isTyping ? (
                              <>
                                <div className="whitespace-pre-line">{mainText}</div>
                                <span className="animate-pulse text-blue-600">▊</span>
                              </>
                            ) : (
                              <div>{formatSummaryText(mainText)}</div>
                            )}
                            {hasAttribution && !message.isTyping && (
                              <div className="text-right mt-2">
                                <span className="text-xs font-semibold text-gray-500">
                                  (Response by Copart Genie)
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                        {/* SQL Query Toggle */}
                        {message.sql && (
                          <div className="mt-4 pt-3 border-t border-slate-200">
                            <button
                              onClick={() => setShowSql(prev => ({ ...prev, [messageId]: !prev[messageId] }))}
                              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800 transition-colors mb-2"
                            >
                              <FaCode className="w-4 h-4" />
                              <span>{showSql[messageId] ? 'Hide' : 'Show'} ClickHouse SQL Query</span>
                            </button>
                            {showSql[messageId] && (
                              <div className="relative mt-2">
                                <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
                                  <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs text-gray-400 font-mono">ClickHouse SQL</span>
                                    <button
                                      onClick={() => copySqlToClipboard(message.sql, messageId)}
                                      className="flex items-center gap-2 text-xs text-gray-400 hover:text-gray-300 transition-colors"
                                    >
                                      {copiedSql[messageId] ? (
                                        <>
                                          <FaCheck className="w-3 h-3" />
                                          <span>Copied!</span>
                          </>
                        ) : (
                                        <>
                                          <FaCopy className="w-3 h-3" />
                                          <span>Copy</span>
                                        </>
                                      )}
                                    </button>
                                  </div>
                                  <pre className="text-green-400 text-xs font-mono overflow-x-auto">
                                    <code>{message.sql}</code>
                                  </pre>
                                </div>
                              </div>
                        )}
                      </div>
                        )}

                      {/* Comparison Visualization */}
                      {(() => {
                        const isComparison = message.query && (
                          message.query.toLowerCase().includes('compare') ||
                          message.query.toLowerCase().includes('vs')
                        );
                        
                        if (isComparison && message.results && message.results.length > 0) {
                          // Group results by yard_number or carrier
                          const groups = {};
                          const metricKey = message.metric === 'upload' ? 'upload_mbps' :
                                          message.metric === 'download' ? 'download_mbps' :
                                          message.metric === 'ping' ? 'ping_ms' :
                                          message.metric === 'signal' ? 'lte_rsrp_dbm' :
                                          message.metric === 'battery' ? 'battery_percent' : 'upload_mbps';
                          
                          message.results.forEach(row => {
                            const groupKey = row.yard_number || row.carrier || 'Unknown';
                            if (!groups[groupKey]) {
                              groups[groupKey] = [];
                            }
                            groups[groupKey].push(row);
                          });
                          
                          // Calculate stats per group
                          const groupStats = {};
                          Object.keys(groups).forEach(key => {
                            const values = groups[key]
                              .map(r => parseFloat(r[metricKey]))
                              .filter(v => !isNaN(v) && v !== null);
                            if (values.length > 0) {
                              groupStats[key] = {
                                count: groups[key].length,
                                avg: values.reduce((a, b) => a + b, 0) / values.length,
                                min: Math.min(...values),
                                max: Math.max(...values),
                              };
                            }
                          });
                          
                          const groupKeys = Object.keys(groupStats).sort();
                          if (groupKeys.length >= 2) {
                            return (
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <div className="mb-3">
                                  <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                                    <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    Comparison Overview
                                  </h3>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {groupKeys.map((key, idx) => {
                                    const stats = groupStats[key];
                                    const isYard = key.match(/^\d+$/);
                                    const label = isYard ? `Yard ${key}` : key;
                                    const bestAvg = Math.max(...Object.values(groupStats).map(s => s.avg));
                                    const isBest = stats.avg === bestAvg;
                                    
                                    return (
                                      <div
                                        key={idx}
                                        className={`p-4 rounded-lg border-2 ${
                                          isBest
                                            ? 'border-green-500 bg-green-50'
                                            : 'border-gray-200 bg-white'
                                        }`}
                                      >
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="font-semibold text-gray-800">{label}</span>
                                          {isBest && (
                                            <span className="text-xs bg-green-500 text-white px-2 py-1 rounded-full font-semibold">
                                              Best
                                            </span>
                                          )}
                                        </div>
                                        <div className="space-y-1 text-sm">
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Average:</span>
                                            <span className="font-semibold text-gray-800">
                                              {stats.avg.toFixed(2)} {metricKey.includes('mbps') ? 'Mbps' : metricKey.includes('ms') ? 'ms' : metricKey.includes('dbm') ? 'dBm' : '%'}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Range:</span>
                                            <span className="text-gray-700">
                                              {stats.min.toFixed(2)} - {stats.max.toFixed(2)}
                                            </span>
                                          </div>
                                          <div className="flex justify-between">
                                            <span className="text-gray-600">Devices:</span>
                                            <span className="text-gray-700">{stats.count}</span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                {groupKeys.length === 2 && (() => {
                                  const [key1, key2] = groupKeys;
                                  const stats1 = groupStats[key1];
                                  const stats2 = groupStats[key2];
                                  const diff = stats1.avg - stats2.avg;
                                  const percentDiff = ((diff / stats2.avg) * 100).toFixed(1);
                                  const winner = diff > 0 ? key1 : key2;
                                  const isYard1 = key1.match(/^\d+$/);
                                  const isYard2 = key2.match(/^\d+$/);
                                  const label1 = isYard1 ? `Yard ${key1}` : key1;
                                  const label2 = isYard2 ? `Yard ${key2}` : key2;
                                  
                                  return (
                                    <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                      <p className="text-sm text-gray-700">
                                        <span className="font-semibold">{label1}</span> averages{' '}
                                        <span className="font-semibold text-blue-700">
                                          {Math.abs(percentDiff)}%
                                        </span>{' '}
                                        {diff > 0 ? 'higher' : 'lower'} than{' '}
                                        <span className="font-semibold">{label2}</span>
                                        {' '}({Math.abs(diff).toFixed(2)} {metricKey.includes('mbps') ? 'Mbps' : metricKey.includes('ms') ? 'ms' : metricKey.includes('dbm') ? 'dBm' : '%'} difference).
                                      </p>
                                    </div>
                                  );
                                })()}
                              </div>
                            );
                          }
                        }
                        return null;
                      })()}

                      {/* Insights Panel */}
                      {message.results && message.results.length > 0 && (() => {
                        const results = message.results;
                        const insights = [];
                        
                        // Calculate metrics
                        const downloadValues = results.map(r => parseFloat(r.download_mbps || r.avg_download || 0)).filter(v => !isNaN(v) && v >= 0);
                        const uploadValues = results.map(r => parseFloat(r.upload_mbps || r.avg_upload || 0)).filter(v => !isNaN(v) && v >= 0);
                        const signalValues = results.map(r => parseFloat(r.lte_rsrp_dbm || r.avg_lte_rsrp || 0)).filter(v => !isNaN(v));
                        
                        // Find worst performing devices
                        const zeroDownload = results.filter(r => {
                          const val = parseFloat(r.download_mbps || r.avg_download || 0);
                          return !isNaN(val) && val === 0;
                        });
                        if (zeroDownload.length > 0) {
                          insights.push({
                            type: 'warning',
                            icon: '⚠️',
                            text: `${zeroDownload.length} device${zeroDownload.length > 1 ? 's' : ''} have 0.00 Mbps download speed`,
                            devices: zeroDownload.map(d => d.android_id).slice(0, 3)
                          });
                        }
                        
                        // Find best performing devices
                        if (downloadValues.length > 0) {
                          const maxDownload = Math.max(...downloadValues);
                          const bestDevice = results.find(r => {
                            const val = parseFloat(r.download_mbps || r.avg_download || 0);
                            return !isNaN(val) && val === maxDownload;
                          });
                          if (bestDevice && maxDownload > 5) {
                            insights.push({
                              type: 'success',
                              icon: '✅',
                              text: `Best performance: ${bestDevice.yard_number ? `Yard ${bestDevice.yard_number}` : 'Device'} with ${maxDownload.toFixed(2)} Mbps download`,
                              device: bestDevice.android_id
                            });
                          }
                        }
                        
                        // Carrier analysis
                        const carrierGroups = {};
                        results.forEach(r => {
                          const carrier = r.carrier || 'Unknown';
                          if (!carrierGroups[carrier]) {
                            carrierGroups[carrier] = [];
                          }
                          carrierGroups[carrier].push(r);
                        });
                        
                        const carrierStats = {};
                        Object.keys(carrierGroups).forEach(carrier => {
                          const carrierDownloads = carrierGroups[carrier]
                            .map(r => parseFloat(r.download_mbps || r.avg_download || 0))
                            .filter(v => !isNaN(v) && v > 0);
                          if (carrierDownloads.length > 0) {
                            carrierStats[carrier] = carrierDownloads.reduce((a, b) => a + b, 0) / carrierDownloads.length;
                          }
                        });
                        
                        const carriers = Object.keys(carrierStats);
                        if (carriers.length > 1) {
                          const sortedCarriers = carriers.sort((a, b) => carrierStats[b] - carrierStats[a]);
                          if (carrierStats[sortedCarriers[0]] > carrierStats[sortedCarriers[1]] * 1.2) {
                            insights.push({
                              type: 'info',
                              icon: '📊',
                              text: `${sortedCarriers[0]} shows better download speeds (${carrierStats[sortedCarriers[0]].toFixed(2)} Mbps) compared to other carriers`
                            });
                          }
                        }
                        
                        // Yard analysis
                        const yardGroups = {};
                        results.forEach(r => {
                          if (r.yard_number) {
                            const yard = r.yard_number;
                            if (!yardGroups[yard]) {
                              yardGroups[yard] = [];
                            }
                            yardGroups[yard].push(r);
                          }
                        });
                        
                        const yardsWithMultipleCarriers = Object.keys(yardGroups).filter(yard => {
                          const carriers = new Set(yardGroups[yard].map(r => r.carrier).filter(c => c && c !== '-'));
                          return carriers.size >= 2;
                        });
                        
                        if (yardsWithMultipleCarriers.length > 0) {
                          insights.push({
                            type: 'info',
                            icon: '📍',
                            text: `${yardsWithMultipleCarriers.length} yard${yardsWithMultipleCarriers.length > 1 ? 's' : ''} have devices from multiple carriers: ${yardsWithMultipleCarriers.map(y => `Yard ${y}`).join(', ')}`
                          });
                        }
                        
                        // Signal strength analysis
                        if (signalValues.length > 0) {
                          const poorSignal = results.filter(r => {
                            const val = parseFloat(r.lte_rsrp_dbm || r.avg_lte_rsrp || 0);
                            return !isNaN(val) && val < -105;
                          });
                          if (poorSignal.length > 0 && poorSignal.length > results.length * 0.3) {
                            insights.push({
                              type: 'warning',
                              icon: '📶',
                              text: `${poorSignal.length} device${poorSignal.length > 1 ? 's' : ''} have poor signal strength (< -105 dBm)`
                            });
                          }
                        }
                        
                        if (insights.length === 0) return null;
                        
                        return (
                          <div className="rounded-lg border border-slate-200 bg-slate-50/80 p-2.5">
                            <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">Insights</h3>
                            <ul className="space-y-1">
                              {insights.map((insight, idx) => (
                                <li key={idx} className={`flex items-start gap-1.5 text-xs ${insight.type === 'warning' ? 'text-amber-800' : insight.type === 'success' ? 'text-emerald-800' : 'text-slate-700'}`}>
                                  <span className="shrink-0">{insight.icon}</span>
                                  <span>{insight.text}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        );
                      })()}

                      {/* Query Details - compact */}
                      {message.parsed && (
                        <div className="text-xs text-slate-500 flex flex-wrap gap-x-2 gap-y-0">
                          <span>Metric: <span className="text-slate-700 font-medium">{message.parsed.metric}</span></span>
                          <span>Limit: <span className="text-slate-700 font-medium">{message.parsed.limit}</span></span>
                          {message.parsed.carrier && (
                            <span>Carrier: <span className="text-slate-700 font-medium">{Array.isArray(message.parsed.carrier) ? message.parsed.carrier.join(', ') : message.parsed.carrier}</span></span>
                          )}
                          <span>Results: <span className="text-slate-700 font-medium">{message.parsed?.intent === 'comparison' ? `${message.count} yard${message.count === 1 ? '' : 's'}` : `${message.count} devices`}</span></span>
                        </div>
                      )}

                      {/* Load More Button */}
                      {(() => {
                        const currentCount = message.results?.length || 0;
                        const pagination = message.pagination;
                        const hasMore = pagination?.hasMore;
                        const isLoading = loadingMore[messageId];
                        
                        return pagination && hasMore ? (
                          <div className="mt-4 flex justify-center">
                            <button
                              onClick={() => handleLoadMore(messageId, currentCount)}
                              disabled={isLoading}
                                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors disabled:bg-blue-300 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
                            >
                              {isLoading ? (
                                <>
                                  <span className="animate-spin">⏳</span>
                                  <span>Loading...</span>
                                </>
                              ) : (
                                <>
                                  <span>View More</span>
                                    <span className="text-sm">({pagination.total - currentCount} more)</span>
                                </>
                              )}
                            </button>
                          </div>
                        ) : null;
                      })()}

                        {/* Count-by-yard table for count answers with yard breakdown */}
                      {message.answer_type === 'count' && message.results && message.results.length > 0 && message.results[0].yard_number != null && message.results[0].device_count != null && (
                        <div className="mt-3 pt-2 border-t border-slate-200">
                          <h3 className="text-xs font-semibold text-slate-800 mb-1.5">By yard</h3>
                          <div className="overflow-x-auto rounded border border-slate-200">
                            <table className="w-full border-collapse text-xs">
                              <thead className="bg-slate-100 text-slate-700">
                                <tr>
                                  <th className="py-1.5 px-2 text-left font-semibold border-b border-slate-200">Yard</th>
                                  <th className="py-1.5 px-2 text-left font-semibold border-b border-slate-200">Devices</th>
                                </tr>
                              </thead>
                              <tbody>
                                {message.results.map((row, idx) => (
                                  <tr key={idx} className={`border-b border-slate-100 ${idx % 2 === 1 ? 'bg-slate-50/50' : ''}`}>
                                    <td className="py-1.5 px-2 font-medium text-slate-800">Yard {row.yard_number}</td>
                                    <td className="py-1.5 px-2 text-slate-700">{row.device_count}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                        {/* Results Table - only when not total-count answer */}
                      {message.results && message.results.length > 0 && message.answer_type !== 'count' && (() => {
                        // Filter and sort results based on upload speed filter
                        let filteredResults = [...message.results];
                        
                        // Apply upload speed filter
                        if (uploadSpeedFilter[messageId]) {
                          if (uploadSpeedFilter[messageId] === 'critical') {
                            filteredResults = filteredResults.filter(r => {
                              const val = parseFloat(r.upload_mbps || r.avg_upload || 0);
                              return isNaN(val) || val === 0;
                            });
                          } else if (uploadSpeedFilter[messageId] === 'poor') {
                            filteredResults = filteredResults.filter(r => {
                              const val = parseFloat(r.upload_mbps || r.avg_upload || 0);
                              return !isNaN(val) && val > 0 && val < 2;
                            });
                          }
                        }
                        
                        // Sort by upload speed if requested
                        if (sortByUpload[messageId]) {
                          filteredResults.sort((a, b) => {
                            const valA = parseFloat(a.upload_mbps || a.avg_upload || 0);
                            const valB = parseFloat(b.upload_mbps || b.avg_upload || 0);
                            if (sortByUpload[messageId] === 'asc') {
                              return valA - valB;
                            } else {
                              return valB - valA;
                            }
                          });
                        }
                        
                        return (
                          <div className="mt-4 pt-3 border-t border-slate-200">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-xs font-semibold text-slate-800 flex items-center gap-1.5">
                                <svg className="w-3.5 h-3.5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                                {message.parsed?.intent === 'comparison' ? `Comparing ${filteredResults.length} yard${filteredResults.length === 1 ? '' : 's'}` : `Results (${filteredResults.length} of ${message.count})`}
                                {uploadSpeedFilter[messageId] && <span className="text-xs text-slate-500 font-normal ml-1">· {uploadSpeedFilter[messageId] === 'critical' ? 'Critical' : 'Poor'}</span>}
                              </h3>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => setSortByUpload(prev => ({
                                    ...prev,
                                    [messageId]: sortByUpload[messageId] === 'asc' ? 'desc' : sortByUpload[messageId] === 'desc' ? null : 'asc'
                                  }))}
                                  className={`px-3 py-1 text-xs rounded-lg font-semibold transition-colors flex items-center gap-1 ${
                                    sortByUpload[messageId] === 'asc'
                                      ? 'bg-green-600 text-white'
                                      : sortByUpload[messageId] === 'desc'
                                      ? 'bg-red-600 text-white'
                                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                  }`}
                                  title="Sort by Upload Speed"
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                                  </svg>
                                  {sortByUpload[messageId] === 'asc' ? '↑ Low to High' : sortByUpload[messageId] === 'desc' ? '↓ High to Low' : 'Sort Upload'}
                                </button>
                                {uploadSpeedFilter[messageId] && (
                                  <button
                                    onClick={() => setUploadSpeedFilter(prev => {
                                      const newState = { ...prev };
                                      delete newState[messageId];
                                      return newState;
                                    })}
                                    className="px-3 py-1 text-xs rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                                  >
                                    Clear Filter
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="overflow-x-auto rounded-lg border border-slate-200">
                          <table className="w-full border-collapse text-xs">
                                <thead className="bg-slate-100 text-slate-700">
                              <tr>
                                    {message.parsed?.intent !== 'comparison' && (
                                      <>
                                        <th className="py-2 px-2 text-left font-semibold border-b border-slate-200">Android ID</th>
                                        <th className="py-2 px-2 text-left font-semibold border-b border-slate-200">Device</th>
                                      </>
                                    )}
                                    <th className="py-2 px-2 text-left font-semibold border-b border-slate-200">Carrier</th>
                                    <th className="py-2 px-2 text-left font-semibold border-b border-slate-200">Yard</th>
                                    <th className="py-2 px-2 text-left font-semibold border-b border-slate-200">Location</th>
                                    <th className="py-2 px-2 text-left font-semibold border-b border-slate-200">Download</th>
                                    <th className="py-2 px-2 text-left font-semibold border-b border-slate-200">Upload</th>
                                    <th className="py-2 px-2 text-left font-semibold border-b border-slate-200">Ping</th>
                                    <th className="py-2 px-2 text-left font-semibold border-b border-slate-200">Signal</th>
                                    <th className="py-2 px-2 text-left font-semibold border-b border-slate-200">Battery</th>
                              </tr>
                            </thead>
                                <tbody>
                                  {filteredResults.map((row, rowIdx) => {
                                const coords = row.geohash ? decodeGeohash(row.geohash) : null;
                                const googleMapsUrl = coords
                                  ? `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}`
                                  : null;

                                const valuePick = (keys) => {
                                  for (const k of keys) {
                                    if (row[k] != null) return row[k];
                                  }
                                  return null;
                                };
                                const fmt = (v) => (v != null && !Number.isNaN(Number(v)) ? Number(v).toFixed(2) : "-");

                                return (
                                    <tr key={rowIdx} className={`border-b border-slate-100 hover:bg-slate-50/80 transition-colors ${rowIdx % 2 === 1 ? 'bg-slate-50/50' : ''}`}>
                                    {message.parsed?.androidId && (
                                        <td className="py-2 px-2 text-slate-600">{row.timestamp ? new Date(row.timestamp).toLocaleString() : "-"}</td>
                                    )}
                                    {message.parsed?.intent !== 'comparison' && (
                                      <>
                                        <td className="py-2 px-2 font-mono text-slate-700">{row.android_id || "-"}</td>
                                        <td className="py-2 px-2 text-slate-700">{row.device_friendly_name || "-"}</td>
                                      </>
                                    )}
                                      <td className="py-2 px-2 text-slate-700">{row.carrier || "-"}</td>
                                      <td className="py-2 px-2">
                                        {row.yard_number ? (
                                          <span className="font-semibold text-sm">Yard {row.yard_number}</span>
                                        ) : (
                                          <span className="text-slate-400">-</span>
                                        )}
                                      </td>
                                      <td className="py-2 px-2 text-slate-700">
                                      {/* Show address if available */}
                                      {row.yard_number && row.yard_address ? (
                                        <div className="space-y-1">
                                          <div className="text-xs text-gray-700">
                                            {row.yard_address}
                                          </div>
                                          {(row.yard_city || row.yard_state) && (
                                            <div className="text-xs text-gray-500">
                                              {[row.yard_city, row.yard_state, row.yard_zip].filter(Boolean).join(', ')}
                                            </div>
                                          )}
                                          {/* Show Google Maps link if yard has coordinates */}
                                          {row.yard_lat && row.yard_lng && (
                                            <a
                                              href={`https://www.google.com/maps/search/?api=1&query=${row.yard_lat},${row.yard_lng}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              title="View yard on Google Maps"
                                              className="text-blue-600 hover:text-blue-800 transition-colors inline-flex items-center gap-1"
                                            >
                                              <FaMapMarkerAlt size="0.75em" />
                                              <span className="text-xs">Map</span>
                                            </a>
                                          )}
                                        </div>
                                      ) : row.yard_number ? (
                                        // Yard number exists but no address in lookup
                                        <div className="text-xs text-gray-400">
                                          Address not found
                                        </div>
                                      ) : coords ? (
                                        // No yard number, show geohash coordinates
                                        <div className="flex items-center gap-2">
                                          <span className="text-xs">
                                            {formatCoordinates(coords)}
                                          </span>
                                          {googleMapsUrl && (
                                            <a
                                              href={googleMapsUrl}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              title={`View on Google Maps (${formatCoordinates(coords)})`}
                                              className="text-blue-600 hover:text-blue-800 transition-colors"
                                            >
                                              <FaMapMarkerAlt size="1em" />
                                            </a>
                                          )}
                                        </div>
                                      ) : (
                                        <span className="text-gray-400">
                                          {row.geohash || "-"}
                                        </span>
                                      )}
                                    </td>
                                    <>
                                        <td className={`py-2 px-2 font-mono font-medium ${(() => {
                                            const val = parseFloat(valuePick(["avg_download", "download_mbps"]));
                                            if (isNaN(val) || val === 0) return 'text-slate-500';
                                            if (val >= 10) return 'text-green-700';
                                            if (val >= 5) return 'text-amber-700';
                                            return 'text-red-700';
                                          })()}`}>{fmt(valuePick(["avg_download", "download_mbps"]))}</td>
                                        <td className={`py-2 px-2 font-mono font-medium ${(() => {
                                            const val = parseFloat(valuePick(["avg_upload", "upload_mbps"]));
                                            if (isNaN(val) || val === 0) return 'text-slate-500';
                                            if (val >= 5) return 'text-green-700';
                                            if (val >= 2) return 'text-amber-700';
                                            return 'text-red-700';
                                          })()}`}>{fmt(valuePick(["avg_upload", "upload_mbps"]))}</td>
                                        <td className={`py-2 px-2 font-mono font-medium ${(() => {
                                            const val = parseFloat(valuePick(["avg_ping", "ping_ms"]));
                                            if (isNaN(val)) return 'text-slate-500';
                                            if (val <= 100) return 'text-green-700';
                                            if (val <= 200) return 'text-amber-700';
                                            return 'text-red-700';
                                          })()}`}>{fmt(valuePick(["avg_ping", "ping_ms"]))}</td>
                                        <td className={`py-2 px-2 font-mono font-medium ${(() => {
                                            const val = parseFloat(valuePick(["avg_lte_rsrp", "lte_rsrp_dbm"]));
                                            if (isNaN(val)) return 'text-slate-500';
                                            if (val >= -90) return 'text-green-700';
                                            if (val >= -105) return 'text-amber-700';
                                            return 'text-red-700';
                                          })()}`}>{fmt(valuePick(["avg_lte_rsrp", "lte_rsrp_dbm"]))}</td>
                                        <td className={`py-2 px-2 font-mono font-medium ${(() => {
                                            const val = parseFloat(valuePick(["avg_battery_percent", "battery_percent"]));
                                            if (isNaN(val)) return 'text-slate-500';
                                            if (val >= 80) return 'text-green-700';
                                            if (val >= 50) return 'text-amber-700';
                                            return 'text-red-700';
                                          })()}`}>
                                        {valuePick(["avg_battery_percent", "battery_percent"]) != null ? Number(valuePick(["avg_battery_percent", "battery_percent"])).toFixed(1) : "-"}
                                      </td>
                                    </>
                                  </tr>
                                );
                                  })}
                            </tbody>
                          </table>
                            </div>
                          </div>
                        );
                      })()}
                        
                        {/* Show message if no results */}
                        {(!message.results || message.results.length === 0) && message.count === 0 && (
                          <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-3 py-2">
                            <p className="text-xs text-slate-600">No results found for this query.</p>
                          </div>
                        )}

                        {/* Follow-up suggestions */}
                        {message.followUpQuestions && message.followUpQuestions.length > 0 && !message.isTyping && (
                          <div className="mt-3 pt-3 border-t border-slate-200">
                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1.5">Suggested follow-ups</p>
                            <div className="flex flex-wrap gap-1.5">
                              {message.followUpQuestions.map((suggestion, idx) => (
                                <button
                                  key={idx}
                                  type="button"
                                  onClick={() => handleSubmit(null, suggestion)}
                                  className="px-2.5 py-1 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs transition-colors border border-slate-200 hover:border-slate-300 text-left"
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
                  {message.type === "user" && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-blue-600 to-blue-700 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                      U
                    </div>
                  )}
              </div>
              );
            })}

              {/* Loading indicator */}
              {loading && (
                <div className="flex gap-2 justify-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-green-400 to-green-600 flex items-center justify-center text-white font-bold text-xs shadow-sm">
                    CG
                  </div>
                  <div className="bg-white rounded-lg px-3 py-2 shadow-md border border-gray-100">
                    <div className="flex items-center gap-2">
                      <span className="text-lg animate-bounce" style={{ animationDuration: '1.5s' }}>
                        🤔
                      </span>
                      <span className="text-xs text-gray-700 font-medium">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Box - Sticky at Bottom */}
          <div className="border-t border-gray-200 bg-white px-3 py-3 shadow-lg">
            <form onSubmit={handleSubmit} className="flex gap-2 items-end">
              <div className="flex-1 relative">
                <textarea
                  ref={inputRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask a question..."
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[40px] max-h-24 text-sm"
                  disabled={loading}
                  rows={1}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSubmit(e);
                    }
                  }}
                  style={{ 
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#cbd5e0 transparent'
                  }}
                />
                <div className="absolute right-2 bottom-2 text-xs text-gray-400">
                  {query.length > 0 && query.length}
                </div>
              </div>
              <button
                type="submit"
                disabled={loading || !query.trim()}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed transition-all font-medium shadow-md hover:shadow-lg disabled:shadow-none flex items-center gap-1 text-sm"
              >
                {loading ? (
                  <>
                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <span>Sending</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                    </svg>
                    <span>Send</span>
                  </>
                )}
              </button>
            </form>
            <p className="text-xs text-gray-500 mt-1 text-center">
              <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-700">Enter</kbd> to send, <kbd className="px-1 py-0.5 bg-gray-100 rounded text-gray-700">Shift+Enter</kbd> for new line
            </p>
          </div>
        </div>
      </>
    );
}
